import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { effectiveCourtHidden } from '@/lib/courtVisibility';

interface EliminationMatch {
  phase: string;
  court: number;
  startTime: string;
  endTime: string;
  category: string;
  participants: string[];
  date: string;
  status?: 'scheduled' | 'completed';
  score?: string;
  winnerTeam?: number | null;
  assignedCourt?: string;    // asignación real (tiempo real por el organizador)
  preAssignedCourt?: string; // preasignación del bloque del schedule
  courtHidden?: boolean;     // el organizador ocultó la cancha preasignada al participante
  matchNumber?: number;
  // Pase directo (bye): el organizador configuró un cuadro donde los mejores
  // clasificados se saltan la primera ronda. El partido llega con un equipo real
  // y el otro pendiente del ganador de la ronda anterior.
  directQualifier?: boolean;
  directSeed?: number;
}

const normalizePhase = (phase: string): string => {
  if (!phase) return '';
  const p = phase.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (p === 'qf' || p.startsWith('cuarto')) return 'cuartos';
  if (p === 'sf' || p.startsWith('semi')) return 'semifinal';
  if (p === 'f' || p === 'final') return 'final';
  if (p.startsWith('octavo') || p === 'r16') return 'octavos';
  if (p.startsWith('r32') || p.startsWith('ronda32')) return 'ronda32';
  if (p.startsWith('r64') || p.startsWith('ronda64')) return 'ronda64';
  return p;
};

interface GroupBlockMatch {
  id?: string;
  match: string;
  time: string;
}

interface GroupBlock {
  group: string;
  category: string;
  court: number;
  startTime: string;
  endTime: string;
  matches: GroupBlockMatch[];
  participants: string[];
  courtHidden?: boolean;
}

export function useTournamentSchedule(eventId: string, category?: string | null, subcategory?: string | null) {
  const [eliminationMatches, setEliminationMatches] = useState<EliminationMatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchSchedule() {
      try {
        const { data, error } = await supabase
          .from('tournament_schedules')
          .select('schedule_data')
          .eq('tournament_id', eventId)
          .single();

        if (error) {
          console.error('Error fetching tournament schedule:', error);
          setLoading(false);
          return;
        }

        if (data && data.schedule_data) {
          const allMatches: EliminationMatch[] = [];
          
          const fullCategory = category && subcategory 
            ? `${category} ${subcategory}` 
            : category;

          // Check if there are any groupBlocks across all dates
          let hasGroupBlocks = false;
          for (const [, dayData] of Object.entries(data.schedule_data as Record<string, any>)) {
            if (dayData.groupBlocks && Array.isArray(dayData.groupBlocks) && dayData.groupBlocks.length > 0) {
              hasGroupBlocks = true;
              break;
            }
          }

          // If groupBlocks exist, fetch registrations to resolve participant names
          let registrationsByGroup: Record<string, { player1_name: string; player2_name: string | null }[]> = {};
          // Also fetch group_calendars to get the canonical team order (A/B/C) per group
          let teamsOrderByGroup: Record<string, { teamOrder: number; name?: string; registrationId?: string }[]> = {};
          if (hasGroupBlocks) {
            const { data: regs } = await supabase
              .from('registrations')
              .select('id, player1_name, player2_name, category, subcategory, "group", registered_at')
              .eq('event_id', eventId)
              .eq('status', 'confirmed')
              .order('registered_at', { ascending: true });

            if (regs) {
              for (const reg of regs) {
                const groupKey = `${reg.category || ''}|${reg.subcategory || ''}|${(reg as any).group || ''}`;
                if (!registrationsByGroup[groupKey]) {
                  registrationsByGroup[groupKey] = [];
                }
                registrationsByGroup[groupKey].push({
                  player1_name: reg.player1_name || '',
                  player2_name: reg.player2_name,
                });
              }
            }

            // Fetch group_calendars for canonical letter ordering
            const { data: cals } = await supabase
              .from('group_calendars' as any)
              .select('group_name, category, teams, schedule_matrix')
              .eq('tournament_id', eventId);
            if (cals) {
              for (const c of cals as any[]) {
                // Key by full category (e.g. "Masculino 1era") + group
                const key = `${(c.category || '').trim()}||${(c.group_name || '').trim()}`;
                const teams = (c.teams && c.teams.length > 0) ? c.teams : (c.schedule_matrix?.teams || []);
                teamsOrderByGroup[key] = teams || [];
              }
            }
          }

          const topLevelSchedule = data.schedule_data as Record<string, any>;
          const topLevelCourtOverrides: Record<string, string> = topLevelSchedule.pickleballCourtOverrides || {};
          // Visibilidad global de canchas preasignadas (organizador). true = ocultas.
          const globalHideCourts = topLevelSchedule._meta?.hideCourtsFromParticipants === true;
          // Regla efectiva: un valor explícito por-partido (courtHidden boolean) GANA
          // sobre el global; si no está definido, hereda del global. Así el organizador
          // puede encender/ocultar canchas individuales aunque el global esté en ocultar,
          // y un cambio global (que reescribe todos los courtHidden) sobrescribe eso.
          const effectiveHidden = (v: any) => effectiveCourtHidden(v, globalHideCourts);

          for (const [date, dayData] of Object.entries(topLevelSchedule)) {
            // Parse individualMatches (existing logic)
            if (dayData.individualMatches && Array.isArray(dayData.individualMatches)) {
              dayData.individualMatches.forEach((match: any) => {
                if (!fullCategory || match.category === fullCategory) {
                  allMatches.push({
                    ...match,
                    date: date,
                    courtHidden: effectiveHidden(match.courtHidden),
                  });
                }
              });
            }

            // Parse groupBlocks (new logic)
            if (dayData.groupBlocks && Array.isArray(dayData.groupBlocks)) {

              dayData.groupBlocks.forEach((block: GroupBlock) => {
                // Build the category string from the block
                const blockCategory = block.category; // e.g. "Masculino 1era"

                // Filter by category if provided
                if (fullCategory && blockCategory !== fullCategory) return;

                // Parse category and subcategory from block.category for registration lookup
                // block.category = "Masculino 1era" → reg.category = "Masculino", reg.subcategory = "1era"
                const catParts = blockCategory.split(' ');
                const regCategory = catParts[0] || '';
                const regSubcategory = catParts.slice(1).join(' ') || '';

                // Compute block-level stable signature (mirrors the organizer's buildPickleballSignature)
                const blockStartTime = block.startTime || (block.matches.length > 0 ? block.matches[0].time : '') || '';
                const blockSig = [
                  date, blockCategory, regSubcategory, 'grupos', block.group, blockStartTime
                ].join('||').toLowerCase();
                const courtOverride: string | undefined = topLevelCourtOverrides[blockSig];
                const effectiveCourt = courtOverride
                  ? courtOverride
                  : block.court ? `Cancha ${block.court}` : undefined;
                // Oculto efectivo: flag explícito del bloque gana; si no, hereda del global
                const blockCourtHidden = effectiveHidden(block.courtHidden);

                const groupKey = `${regCategory}|${regSubcategory}|${block.group}`;
                const groupTeams = registrationsByGroup[groupKey] || [];

                // Try canonical ordering from group_calendars first
                const calKey = `${blockCategory}||${block.group}`;
                const canonicalTeams = teamsOrderByGroup[calKey] || [];

                // Build the ordered team list (index 0 = letter A, 1 = B, 2 = C)
                const normForCmp = (s: string) =>
                  (s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                    .replace(/\s*\/\s*/g, ' / ').replace(/\s+/g, ' ');

                const orderedTeams: { player1_name: string; player2_name: string | null }[] = [];
                if (canonicalTeams.length > 0) {
                  const sorted = [...canonicalTeams].sort((a: any, b: any) => (a.teamOrder || 0) - (b.teamOrder || 0));
                  for (const ct of sorted) {
                    // Slot hueco (pareja quitada o roster sin asignar): se conserva
                    // la POSICIÓN con un marcador vacío para que las letras (índices)
                    // de las demás parejas no se recorran. Se muestra "Por asignar".
                    const isGap = !!(ct as any).registrationId &&
                      String((ct as any).registrationId).startsWith('placeholder-');
                    if (isGap) {
                      orderedTeams.push({ player1_name: 'Por asignar', player2_name: null });
                      continue;
                    }
                    const ctName = normForCmp((ct as any).name || '');
                    const matchedReg = groupTeams.find(gt => {
                      const gtName = normForCmp(`${gt.player1_name}${gt.player2_name ? ' / ' + gt.player2_name : ''}`);
                      return gtName === ctName;
                    });
                    if (matchedReg) {
                      orderedTeams.push(matchedReg);
                    } else {
                      // Fallback: split team name into player1/player2
                      const parts = ((ct as any).name || '').split(' / ');
                      orderedTeams.push({
                        player1_name: parts[0]?.trim() || '',
                        player2_name: parts[1]?.trim() || null,
                      });
                    }
                  }
                } else {
                  // Last resort: use registration order
                  orderedTeams.push(...groupTeams);
                }

                block.matches.forEach((match: GroupBlockMatch) => {
                  // Extract team letters from match label: "G-Masculino 1era-A-AB" → "AB"
                  const parts = match.match.split('-');
                  const teamCodes = parts[parts.length - 1]; // "AB", "AC", "BC"

                  const participants: string[] = [];
                  if (teamCodes.length === 2 && orderedTeams.length > 0) {
                    const idx1 = teamCodes.charCodeAt(0) - 65; // A=0, B=1, C=2
                    const idx2 = teamCodes.charCodeAt(1) - 65;

                    const team1 = orderedTeams[idx1];
                    const team2 = orderedTeams[idx2];

                    const formatName = (t: { player1_name: string; player2_name: string | null } | undefined) => {
                      if (!t) return 'Por asignar';
                      return t.player2_name
                        ? `${t.player1_name} / ${t.player2_name}`
                        : t.player1_name;
                    };

                    participants.push(formatName(team1), formatName(team2));
                  }

                  allMatches.push({
                    phase: block.group, // "Grupo A"
                    court: block.court,
                    startTime: match.time,
                    endTime: block.endTime,
                    category: blockCategory,
                    participants,
                    date: date,
                    // Si está oculta, no exponemos la cancha preasignada al jugador
                    preAssignedCourt: blockCourtHidden ? undefined : effectiveCourt,
                    courtHidden: blockCourtHidden,
                  });
                });
              });
            }
          }
          
          // Enrich matches with results from tournament_match_results
          const { data: matchResults } = await supabase
            .from('tournament_match_results')
            .select('team1_player1, team1_player2, team2_player1, team2_player2, winner_team, score, group_name, phase, match_number, category, subcategory')
            .eq('tournament_id', eventId);

          if (matchResults && matchResults.length > 0) {
            const normalizeForCompare = (name: string) => {
              if (!name) return '';
              return name
                .trim()
                .toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .replace(/\s*\/\s*/g, ' / ')
                .replace(/\s*-\s*/g, ' / ')
                .replace(/\s+/g, ' ');
            };

            for (const m of allMatches) {
              if (!m.participants || m.participants.length < 2) continue;

              const p1 = normalizeForCompare(m.participants[0]);
              const p2 = normalizeForCompare(m.participants[1]);

              // Parse category/subcategory from match.category (e.g. "Masculino 1era")
              const mCatParts = m.category?.split(' ') || [];
              const mCategory = mCatParts[0] || '';
              const mSubcategory = mCatParts.slice(1).join(' ') || '';

              const result = matchResults.find(r => {
                // Phase compatibility check first: prevent group results from applying to elimination and vice versa
                const mPhaseNorm = normalizePhase(m.phase);
                const rPhaseNorm = normalizePhase(r.phase || r.group_name || '');
                const bothGroupPhases = mPhaseNorm.startsWith('grupo') && rPhaseNorm.startsWith('grupo');
                const phasesCompatible = !mPhaseNorm || !rPhaseNorm || mPhaseNorm === rPhaseNorm || bothGroupPhases;
                if (!phasesCompatible) return false;

                // Strategy B (highest priority): Exact full team name match
                const t1Full = normalizeForCompare(
                  r.team1_player2 ? `${r.team1_player1} / ${r.team1_player2}` : r.team1_player1 || ''
                );
                const t2Full = normalizeForCompare(
                  r.team2_player2 ? `${r.team2_player1} / ${r.team2_player2}` : r.team2_player1 || ''
                );

                if ((p1 === t1Full && p2 === t2Full) || (p1 === t2Full && p2 === t1Full)) return true;

                // Strategy A (fallback): Match by phase + category + subcategory + matchNumber (elimination ONLY)
                // Only use when match has placeholder participants (no real names assigned yet)
                const isGroupPhase = mPhaseNorm.startsWith('grupo') || 
                                     m.phase?.toLowerCase().startsWith('grupo');
                const hasPlaceholderParticipants = !m.participants || m.participants.length < 2 ||
                  m.participants.some(p => !p || p === 'Por definir' || p === 'TBD' || p === 'Por asignar' || p.trim() === '');
                if (!isGroupPhase && hasPlaceholderParticipants && m.matchNumber != null && r.match_number != null) {
                  const phaseMatch = mPhaseNorm === rPhaseNorm;
                  const catMatch = normalizeForCompare(mCategory) === normalizeForCompare(r.category || '');
                  const subMatch = normalizeForCompare(mSubcategory) === normalizeForCompare(r.subcategory || '');
                  if (phaseMatch && catMatch && subMatch && m.matchNumber === r.match_number) {
                    return true;
                  }
                }

                // Strategy C (last resort): Individual player name matching
                const rPlayers = [
                  normalizeForCompare(r.team1_player1 || ''),
                  normalizeForCompare(r.team1_player2 || ''),
                  normalizeForCompare(r.team2_player1 || ''),
                  normalizeForCompare(r.team2_player2 || '')
                ].filter(Boolean);

                const mPlayers = [
                  ...p1.split(' / ').map(n => normalizeForCompare(n)),
                  ...p2.split(' / ').map(n => normalizeForCompare(n))
                ].filter(Boolean);

                if (mPlayers.length >= 2 && rPlayers.length >= 2) {
                  const matchCount = mPlayers.filter(mp => rPlayers.some(rp => rp === mp)).length;
                  return matchCount >= Math.min(mPlayers.length, rPlayers.length);
                }

                return false;
              });

              if (result) {
                m.status = 'completed';
                m.score = result.score || undefined;
                if (result.winner_team) {
                  const t1Full = normalizeForCompare(
                    result.team1_player2 ? `${result.team1_player1} / ${result.team1_player2}` : result.team1_player1 || ''
                  );
                  const isP1Team1 = p1 === t1Full;
                  m.winnerTeam = isP1Team1 ? result.winner_team : (result.winner_team === 1 ? 2 : result.winner_team === 2 ? 1 : result.winner_team);
                } else {
                  m.winnerTeam = null;
                }
              }
            }
          }

          // Enrich matches with assigned court info from assignedMatches
          const normalizeForCourt = (name: string) => name?.trim().toLowerCase().replace(/\s*-\s*/g, ' / ').replace(/\s+/g, ' ') || '';

          // Collect all assignedMatches from all dates
          for (const [, dayData] of Object.entries(topLevelSchedule)) {
            if (dayData.assignedMatches && Array.isArray(dayData.assignedMatches)) {
              for (const block of dayData.assignedMatches) {
                const courtName = block.courtName || '';
                if (!courtName || !block.matches || !Array.isArray(block.matches)) continue;
                
                for (const am of block.matches) {
                  const t1 = normalizeForCourt(am.team1 || '');
                  const t2 = normalizeForCourt(am.team2 || '');
                  if (!t1 || !t2) continue;
                  
                  for (const m of allMatches) {
                    if (!m.participants || m.participants.length < 2) continue;
                    const mp1 = normalizeForCourt(m.participants[0]);
                    const mp2 = normalizeForCourt(m.participants[1]);
                    if ((mp1 === t1 && mp2 === t2) || (mp1 === t2 && mp2 === t1)) {
                      m.assignedCourt = courtName;
                    }
                  }
                }
              }
            }
          }

          // Also check top-level assignedMatches (not nested in dates)
          if (topLevelSchedule.assignedMatches && Array.isArray(topLevelSchedule.assignedMatches)) {
            for (const block of topLevelSchedule.assignedMatches) {
              const courtName = block.courtName || '';
              if (!courtName || !block.matches || !Array.isArray(block.matches)) continue;
              
              for (const am of block.matches) {
                const t1 = normalizeForCourt(am.team1 || '');
                const t2 = normalizeForCourt(am.team2 || '');
                if (!t1 || !t2) continue;
                
                for (const m of allMatches) {
                  if (!m.participants || m.participants.length < 2) continue;
                  const mp1 = normalizeForCourt(m.participants[0]);
                  const mp2 = normalizeForCourt(m.participants[1]);
                  if ((mp1 === t1 && mp2 === t2) || (mp1 === t2 && mp2 === t1)) {
                    m.assignedCourt = courtName;
                  }
                }
              }
            }
          }

          setEliminationMatches(allMatches);
        }
      } catch (err) {
        console.error('Exception fetching schedule:', err);
      } finally {
        setLoading(false);
      }
    }

    if (eventId) {
      fetchSchedule();

      const channel = supabase
        .channel(`tournament-schedule-${eventId}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'tournament_schedules',
          filter: `tournament_id=eq.${eventId}`
        }, () => {
          fetchSchedule();
        })
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'tournament_match_results',
          filter: `tournament_id=eq.${eventId}`
        }, () => {
          fetchSchedule();
        })
        .subscribe();

      const pollInterval = setInterval(() => {
        fetchSchedule();
      }, 30000);

      return () => {
        supabase.removeChannel(channel);
        clearInterval(pollInterval);
      };
    }
  }, [eventId, category, subcategory]);

  return { eliminationMatches, loading };
}
